import isEqual from 'lodash/isEqual';
import MpSdk, { CommonMpSdk } from 'matterport';
import React, { useRef, useState } from 'react';
import { TDR } from 'tdr-common';
import { useDebouncedCallback } from 'use-debounce';
import { distanceBetweenPoints, isPointInView, sortTablesByPos, sortTablesByCenter } from '../../helpers/geometry';
import { ClosestTableInfo } from './ClosestTableInfo';
import { LostYourWay } from './LostYourWay';
import { useTableSelectionContext } from '@/context/TableSelectionContext';
import { useSmoothState } from '../../hooks/useSmoothState';

// Constants for camera pose and table detection thresholds
export const LOADING_COORDINATES = { x: 0, y: 0, z: 0 };

/**
 * Maximum distance (in meters) from camera to consider a table "nearby".
 * Tables beyond this distance are filtered out from visibility calculations.
 */
const TABLE_VISIBILITY_DISTANCE_METERS = 20.5;

/**
 * Debounce delay (in milliseconds) for pose calculations.
 * Prevents excessive recalculations when camera is moving rapidly.
 */
const POSE_DEBOUNCE_MS = 500;

/**
 * Minimum rotation change (in radians) to trigger direction vector update.
 * 0.01 radians ≈ 0.57 degrees - filters out jitter from tiny camera movements.
 */
const DIRECTION_ROTATION_THRESHOLD_RADIANS = 0.01;

/**
 * Minimum distance change (in meters) to trigger direction vector update.
 * Filters out jitter from tiny camera movements.
 */
const DIRECTION_DISTANCE_THRESHOLD_METERS = 0.1;

export type MatterportEnvironmentNoticeProps = {
	sdk: MpSdk;
	tables: TDR.Table[]
	availableTables: TDR.Table[]
	hiddenTables: Set<string>;
	gotoTable: (sdk: MpSdk, table: TDR.Table) => Promise<void | string>
	closestVisibleTable: TDR.Table | null;
	setClosestVisibleTable: (table: TDR.Table | null) => void;
	setDirectionVector: (vector: {x: number, y: number, z:number, length: number}) => void;
}

export const MatterportEnvironmentNotice: React.FC<MatterportEnvironmentNoticeProps> = ({ gotoTable, sdk, tables, availableTables, hiddenTables, setClosestVisibleTable, closestVisibleTable, setDirectionVector }) => {
	// Use useSmoothState for automatic bailout when table ID hasn't changed
	const [closestAvailableTable, setClosestAvailableTable] = useSmoothState<TDR.Table | null>(null);
	const [pos, setPos] = useState(LOADING_COORDINATES);
	const [rotation, setRotation] = useState({ x: 0, y: 0 });
	const [moved, setMoved] = useState<boolean | undefined>(undefined);

	const prevDirectionRef = useRef<{rotationY: number, length: number} | null>(null);

	const { setGlobalNearestTable, globalNearestTable } = useTableSelectionContext();

	const debouncedNoticePose = useDebouncedCallback((pose) => calculateNoticePosition(pose), POSE_DEBOUNCE_MS);
	const debouncedMovedMarker = useDebouncedCallback((pose) => recordFirstMovement(pose), POSE_DEBOUNCE_MS);


	const calculateNoticePosition = (pose: CommonMpSdk.Camera.Pose) => {
		const filtered = tables.filter(table => {
			return !hiddenTables.has(table.id) && distanceBetweenPoints(table.pos, pose.position) < TABLE_VISIBILITY_DISTANCE_METERS && isPointInView(pose.position, pose.rotation, table.pos);
		});
		if (!filtered.length) {
			if (availableTables.length > 0) {
				const sorted = sortTablesByPos(availableTables, pose.position);
				// useSmoothState automatically bails out if ID hasn't changed
				setClosestAvailableTable(sorted[0]);
			}
			else {
				// useSmoothState automatically bails out if already null
				setClosestAvailableTable(null);
			}
		}
	};

	const calculateTablePosition = (pose: CommonMpSdk.Camera.Pose) => {
		const filtered = tables.filter(table => {
			return !hiddenTables.has(table.id) && distanceBetweenPoints(table.pos, pose.position) < TABLE_VISIBILITY_DISTANCE_METERS && isPointInView(pose.position, pose.rotation, table.pos);
		});
		if (filtered.length) {
			const sorted = sortTablesByCenter(filtered, pose.position, pose.rotation);
			const newClosestTable = sorted[0];

			// useSmoothState in parent automatically bails out if ID hasn't changed
			setClosestVisibleTable(newClosestTable);

			// Only update context if table actually changed (prevents 60 FPS cascade re-renders)
			if (newClosestTable?.id !== globalNearestTable?.id) {
				setGlobalNearestTable(newClosestTable);
			}

			// useSmoothState automatically bails out if already null
			setClosestAvailableTable(null);

			// Calculate direction vector
			const directionVector = {
				x: newClosestTable.pos.x - pose.position.x,
				y: newClosestTable.pos.y - pose.position.y,
				z: newClosestTable.pos.z - pose.position.z
			};
			const length = Math.sqrt(directionVector.x * directionVector.x + directionVector.y * directionVector.y + directionVector.z * directionVector.z);
			directionVector.x /= length;
			directionVector.y /= length;
			directionVector.z /= length;
			const rotationY = Math.atan2(directionVector.x, directionVector.z);

			// Only update direction if changed significantly (prevents jitter from tiny camera movements)
			const prev = prevDirectionRef.current;
			const rotationChanged = !prev || Math.abs(rotationY - prev.rotationY) > DIRECTION_ROTATION_THRESHOLD_RADIANS;
			const lengthChanged = !prev || Math.abs(length - prev.length) > DIRECTION_DISTANCE_THRESHOLD_METERS;

			if (rotationChanged || lengthChanged) {
				setDirectionVector({ x: 0, y: rotationY, z: 0, length: length });
				prevDirectionRef.current = { rotationY, length };
			}
		}
		else {
			// useSmoothState in parent automatically bails out if already null
			setClosestVisibleTable(null);

			// Only update context if table actually changed
			if (globalNearestTable !== null) {
				setGlobalNearestTable(null);
			}
		}

		setPos(pose.position);
		setRotation(pose.rotation);
	};

	const recordFirstMovement = (pose: CommonMpSdk.Camera.Pose) => {

		if (!isEqual(pose, LOADING_COORDINATES) && moved !== true) {
			if (moved === undefined) {
				setMoved(false);
			}
			else if (moved === false) {
				setMoved(true);
			}
		}
	};

	// Subscribe to camera pose updates (fires at 60 FPS)
	if (sdk?.Camera?.pose) {
		sdk.Camera.pose.subscribe(function (pose) {
			if (pose?.position && pose?.projection && tables) {
				if (!isEqual(pose.position, pos) || !isEqual(pose.rotation, rotation)) {
					calculateTablePosition(pose);
					debouncedNoticePose(pose);
					debouncedMovedMarker(pose);
				}
			}
		});
	}


	const onClosestTableHeaderClick = (table: TDR.Table) => {
		gotoTable(sdk, table);
	};

	if (closestVisibleTable && !hiddenTables.has(closestVisibleTable.id)) {
		return <ClosestTableInfo table={closestVisibleTable} onClick={onClosestTableHeaderClick} availableTables={availableTables} />;
	}

	if (closestAvailableTable) {
		return <LostYourWay table={closestAvailableTable} navigateToPin={gotoTable.bind(null, sdk)} />;
	}

	return null;
};